import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Landing from "./pages/Landing";
import Admin from "./pages/Admin";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import Documents from "@/pages/Documents";
import { ClerkProvider, SignIn, SignUp, SignedIn, SignedOut } from '@clerk/clerk-react';
import { Footer } from "@/components/layout/Footer";
import { PrivateRoute } from "@/components/PrivateRoute";
import { ApiDebugPanel } from "@/components/debug/ApiDebugPanel";
import * as Sentry from "@sentry/react";
import { SentryTest } from "@/components/debug/SentryTest";
import { initPostHog } from "@/config/posthog";
import { withErrorBoundary } from '@/components/error-boundary/withErrorBoundary';
import { LoggingPanel } from '@/components/debug/LoggingPanel';

if (!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Initialize PostHog
initPostHog();

// Custom appearance for Clerk components
const clerkAppearance = {
  layout: {
    logoImageUrl: '/docverifai-dv-logo.png',
    logoLinkUrl: '/',
  },
  elements: {
    logoImage: { width: '180px', height: '60px', maxWidth: '180px' },
    rootBox: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      margin: '0 auto',
    },
    card: {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderRadius: '8px',
      maxWidth: '400px',
      width: '100%',
    },
  },
};

/**
 * The fixed app footer belongs to the signed-in product, not the public landing page.
 */
const AppFooter: React.FC = () => {
  const { pathname } = useLocation();
  if (pathname === "/") return null;
  return <Footer />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl={import.meta.env.VITE_CLERK_AFTER_SIGN_IN_URL || "/app"}
        signUpFallbackRedirectUrl={import.meta.env.VITE_CLERK_AFTER_SIGN_UP_URL || "/app"}
        appearance={clerkAppearance}
        localization={{
          signIn: { start: { title: 'Sign in to DocVerifAI' } },
          signUp: { start: { title: 'Create your DocVerifAI account' } },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Routes>
              {/* Public landing page */}
              <Route path="/" element={<Landing />} />

              {/* Auth routes */}
              <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" />} />
              <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" />} />

              {/* Use existing pages with their own headers but add the global footer */}
              <Route path="/app" element={
                <div className="pb-16">
                  <PrivateRoute>
                    <Index />
                  </PrivateRoute>
                </div>
              } />

              <Route path="/app/documents" element={
                <div className="pb-16">
                  <PrivateRoute>
                    <Documents />
                  </PrivateRoute>
                </div>
              } />

              <Route path="/app/admin" element={
                <div className="pb-16">
                  <PrivateRoute requiredRole="admin">
                    <Admin />
                  </PrivateRoute>
                </div>
              } />

              {/* Superseded paths kept working */}
              <Route path="/documents" element={<Navigate to="/app/documents" replace />} />
              <Route path="/admin" element={<Navigate to="/app/admin" replace />} />

              {/* Public routes */}
              <Route path="/privacy" element={
                <div className="pb-16">
                  <Privacy />
                </div>
              } />

              <Route path="/terms" element={
                <div className="pb-16">
                  <Terms />
                </div>
              } />

              {/* Catch-all route */}
              <Route path="*" element={
                <div className="pb-16">
                  <NotFound />
                </div>
              } />
            </Routes>
            <AppFooter />
          </TooltipProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </BrowserRouter>
  );
};

// Use our new withErrorBoundary HOC
export default withErrorBoundary(App, { feature: 'app' });
